/* ==========================================================================
   OREVYX · Ski Mask PDP - ovx-ski.js
   Loaded (deferred) only on templates/product.ski-mask.json.
   Modules: flash clock · countdown bar · marquee · snap slider (gallery,
   mini reviews, review cards) · bundle engine + cart · sticky ATC · arrives line · videos.
   No globals except window.ovxSki. Re-initialises on shopify:section:load.
   ========================================================================== */
(() => {
  "use strict";

  const REDUCED = window.matchMedia("(prefers-reduced-motion: reduce)");
  const ns = (window.ovxSki = window.ovxSki || {});
  ns.flash = ns.flash || { active: false, offer: "", pack: false, gift: false, start: 0, end: 0, preview: false };


  /* ---------- utilities ------------------------------------------------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const pad2 = (n) => String(n).padStart(2, "0");
  const debounce = (fn, ms) => {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  };
  const roundHalfUp = (x) => Math.floor(x + 0.5);

  // Shopify money formatting with the placeholder variants (amount,
  // amount_no_decimals, *_with_comma_separator, *_with_space_separator,
  // *_with_apostrophe_separator, amount_with_period_and_space_separator).
  function formatMoney(cents, format) {
    const fmt = format || "${{amount}}";
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, ph) => {
      let th = ",", dec = ".", decimals = 2;
      if (ph.includes("comma_separator")) { th = "."; dec = ","; }
      else if (ph.includes("period_and_space")) { th = " "; dec = "."; }
      else if (ph.includes("space_separator")) { th = " "; dec = ","; }
      else if (ph.includes("apostrophe")) { th = "'"; dec = "."; }
      if (ph.includes("no_decimals")) decimals = 0;
      const num = (Math.round(cents) / 100).toFixed(decimals);
      const [int, frac] = num.split(".");
      const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return frac !== undefined ? `${grouped}${dec}${frac}` : grouped;
    });
  }

  // Per-section cleanup registry so the theme editor can unload/reload
  // sections without leaking timers or observers.
  const cleanups = new WeakMap();
  const registerCleanup = (el, fn) => {
    const list = cleanups.get(el) || [];
    list.push(fn);
    cleanups.set(el, list);
  };
  const runCleanups = (el) => {
    (cleanups.get(el) || []).forEach((fn) => { try { fn(); } catch (e) { /* noop */ } });
    cleanups.delete(el);
  };

  /* ---------- flash clock (single source of truth in JS) ----------------- */
  // The daily rotation: day N since rotation.start (store-local calendar day) runs
  // offers[N mod offers.length], midnight to midnight in rotation.tz. The server
  // renders today's state; this clock recomputes it every second so the bar, the
  // marquee, the gift rows and the cart nudge flip at midnight without a reload.
  // Day numbers are Julian day numbers, the same integer formula as the Liquid
  // snippet and the discount function.
  const flash = (() => {
    let rotation = null; // { start: "YYYY-MM-DD", offers: ["gift", "pack"], tz: "America/New_York" }
    let today = null;    // server-rendered { offer, start, end } (also the editor preview)
    let preview = false;
    let offset = 0;      // serverNow - clientNow (seconds)
    let fmt = null;
    const listeners = new Set();
    const tickers = new Set();
    let timer = null;
    let lastKey = "";

    const jdn = (y, m, d) => {
      const a = Math.floor((14 - m) / 12);
      const y2 = y + 4800 - a;
      const m2 = m + 12 * a - 3;
      return d + Math.floor((153 * m2 + 2) / 5) + 365 * y2 + Math.floor(y2 / 4) - Math.floor(y2 / 100) + Math.floor(y2 / 400) - 32045;
    };
    // Calendar parts of an epoch (ms) in the store's timezone.
    const parts = (ms) => {
      if (!fmt) fmt = new Intl.DateTimeFormat("en-US", { timeZone: rotation.tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
      const o = {};
      fmt.formatToParts(new Date(ms)).forEach((p) => { if (p.type !== "literal") o[p.type] = Number(p.value); });
      if (o.hour === 24) o.hour = 0;
      return o;
    };
    const tzOffsetMs = (ms) => { const p = parts(ms); return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - ms; };
    // Epoch (ms) of 00:00:00 store time on a calendar day; two passes so a DST change that day cannot skew it.
    const localMidnightMs = (y, m, d) => {
      const guess = Date.UTC(y, m - 1, d);
      const off1 = tzOffsetMs(guess);
      let t = guess - off1;
      const off2 = tzOffsetMs(t);
      if (off2 !== off1) t = guess - off2;
      return t;
    };
    const offerAt = (y, m, d) => {
      const [sy, sm, sd] = String(rotation.start).split("-").map(Number);
      const idx = jdn(y, m, d) - jdn(sy, sm, sd);
      const n = rotation.offers.length;
      return rotation.offers[((idx % n) + n) % n];
    };

    function load() {
      const node = qs("[data-ovx-ski-flash]");
      if (!node) return;
      try {
        const data = JSON.parse(node.textContent || "{}");
        const r = data.rotation;
        rotation = r && typeof r.start === "string" && /^\d{4}-\d{2}-\d{2}$/.test(r.start) && Array.isArray(r.offers) && r.offers.length && typeof r.tz === "string" ? r : null;
        today = data.today && data.today.offer ? data.today : null;
        preview = !!data.preview;
        if (data.now) offset = Number(data.now) - Math.floor(Date.now() / 1000);
        fmt = null;
      } catch (e) { rotation = null; today = null; }
    }
    const now = () => Math.floor(Date.now() / 1000) + offset;
    // Today's window from the rotation, computed in the store timezone (Intl); null when not configured.
    function computeWindow(t) {
      if (!rotation) return null;
      try {
        const p = parts(t * 1000);
        const start = Math.floor(localMidnightMs(p.year, p.month, p.day) / 1000);
        const nx = new Date(Date.UTC(p.year, p.month - 1, p.day + 1));
        const end = Math.floor(localMidnightMs(nx.getUTCFullYear(), nx.getUTCMonth() + 1, nx.getUTCDate()) / 1000) - 1;
        return { offer: offerAt(p.year, p.month, p.day), start, end };
      } catch (e) { return null; }
    }
    // No Intl (very old browsers): extend the server's window by whole days, cycling the offers.
    function fallbackWindow(t) {
      if (!today) return null;
      let w = { offer: today.offer, start: today.start, end: today.end };
      if (!rotation) return t >= w.start && t <= w.end ? w : null;
      const n = rotation.offers.length;
      let i = Math.max(0, rotation.offers.indexOf(today.offer));
      let guard = 0;
      while (t > w.end && guard++ < 1000) { i = (i + 1) % n; w = { offer: rotation.offers[i], start: w.end + 1, end: w.end + 86400 }; }
      return t >= w.start ? w : null;
    }
    function state() {
      const t = now();
      const w = computeWindow(t) || fallbackWindow(t);
      const s = w
        ? { active: true, offer: w.offer, pack: w.offer === "pack", gift: w.offer === "gift", start: w.start, end: w.end, preview }
        : { active: false, offer: "", pack: false, gift: false, start: 0, end: 0, preview: false };
      ns.flash = s;
      return s;
    }
    function tick() {
      const s = state();
      const key = `${s.active}:${s.offer}:${s.end}`;
      if (key !== lastKey) {
        lastKey = key;
        listeners.forEach((fn) => { try { fn(s); } catch (e) { /* noop */ } });
      }
      tickers.forEach((fn) => { try { fn(s, now()); } catch (e) { /* noop */ } });
    }
    function start() {
      stop();
      timer = setInterval(tick, 1000);
      tick();
    }
    function stop() { if (timer) clearInterval(timer); timer = null; }
    document.addEventListener("visibilitychange", () => (document.hidden ? stop() : start()));
    return {
      load,
      now,
      state,
      start,
      onChange(fn) { listeners.add(fn); fn(state()); return () => listeners.delete(fn); },
      onTick(fn) { tickers.add(fn); return () => tickers.delete(fn); },
    };
  })();
  ns.flashClock = flash;

  /* ---------- 6.2 countdown bar ------------------------------------------ */
  function initBar(bar) {
    const KEY = "ovx-ski-bar-dismissed";
    // Memory is the PRIMARY source of truth; sessionStorage is only a bonus.
    // In a Private tab storage can throw, and the old code then lost the
    // dismissal on the next section re-init.
    let isDismissed = ns.barDismissed === true;
    if (!isDismissed) { try { isDismissed = sessionStorage.getItem(KEY) === "1"; } catch (e) { /* noop */ } }
    if (isDismissed) { ns.barDismissed = true; bar.hidden = true; return; }

    const title = qs("[data-bar-title]", bar);
    const sub = qs("[data-bar-sub]", bar);
    const ends = qs("[data-bar-ends]", bar);
    const d = qs("[data-bar-days]", bar), h = qs("[data-bar-hours]", bar), m = qs("[data-bar-mins]", bar), s = qs("[data-bar-secs]", bar);
    const flashTitle = bar.dataset.flashTitle || "";
    const flashSubs = { pack: bar.dataset.flashSubtitlePack || "", gift: bar.dataset.flashSubtitleGift || "" };
    const fallback = bar.dataset.fallbackTitle || "";
    const endsTpl = bar.dataset.endsTemplate || "Ends {{ date }}";

    // The clock re-renders every second. Without this guard, render() sets
    // bar.hidden = false again a moment after the close button hides it, so the
    // X looked like it did nothing whenever a flash sale was running.
    const render = (st) => {
      if (isDismissed) { bar.hidden = true; return; }
      const previewTag = qs("[data-bar-preview]", bar);
      if (previewTag) previewTag.hidden = !(st.active && st.preview);
      if (st.active) {
        const flashSub = flashSubs[st.offer] || "";
        bar.classList.add("ovx-ski-bar--flash");
        bar.dataset.offer = st.offer;
        if (title) title.textContent = flashTitle;
        if (sub) { sub.textContent = flashSub; sub.hidden = !flashSub; }
        if (ends) {
          const dt = new Date(st.end * 1000);
          ends.textContent = endsTpl.replace("{{ date }}", dt.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }));
        }
        bar.hidden = false;
      } else {
        bar.classList.remove("ovx-ski-bar--flash");
        bar.dataset.offer = "";
        if (title) title.textContent = fallback;
        if (sub) { sub.textContent = ""; sub.hidden = true; }
        bar.hidden = !fallback;
      }
    };
    const tick = (st, t) => {
      if (!st.active) return;
      let left = Math.max(0, st.end - t);
      const days = Math.floor(left / 86400); left -= days * 86400;
      const hours = Math.floor(left / 3600); left -= hours * 3600;
      const mins = Math.floor(left / 60); left -= mins * 60;
      if (d) d.textContent = pad2(days);
      if (h) h.textContent = pad2(hours);
      if (m) m.textContent = pad2(mins);
      if (s) s.textContent = pad2(left);
    };
    registerCleanup(bar, flash.onChange(render));
    registerCleanup(bar, flash.onTick(tick));

    // Expose the bar height so the desktop header can stick right underneath it.
    const setH = () => document.documentElement.style.setProperty("--ovx-ski-bar-h", bar.hidden ? "0px" : `${bar.getBoundingClientRect().height}px`);
    if ("ResizeObserver" in window) { const ro = new ResizeObserver(setH); ro.observe(bar); registerCleanup(bar, () => ro.disconnect()); }
    setH();
    const mo = new MutationObserver(setH); mo.observe(bar, { attributes: true, attributeFilter: ["hidden", "class"] }); registerCleanup(bar, () => mo.disconnect());

    const close = qs("[data-bar-close]", bar);
    if (close) {
      let done = false;
      const dismiss = (e) => {
        if (e) e.preventDefault();
        if (done) return;
        done = true;
        isDismissed = true;
        ns.barDismissed = true;           // survives a section re-init even if storage throws
        bar.hidden = true;
        setH();
        try { sessionStorage.setItem(KEY, "1"); } catch (e2) { /* Private tab: memory still holds */ }
      };
      // click covers mouse, keyboard and normal taps; touchend is the belt and
      // braces for iOS, guarded so the pair cannot double-fire.
      close.addEventListener("click", dismiss);
      close.addEventListener("touchend", dismiss, { passive: false });
    }
  }

  /* ---------- 6.3 marquee -------------------------------------------------- */
  function initMarquee(el) {
    const track = qs("[data-marquee-track]", el);
    const groups = qsa("[data-marquee-group]", el);
    if (!track || !groups.length) return;
    const speed = Number(el.dataset.speed) || 40; // px per second

    const measure = () => {
      const w = groups[0].getBoundingClientRect().width;
      if (w > 0) el.style.setProperty("--ovx-ski-marquee-duration", `${Math.max(8, w / speed)}s`);
      const anyVisible = qsa("[data-marquee-item]", groups[0]).some((i) => !i.hidden);
      el.hidden = !anyVisible;
    };
    const applyVisibility = (st, t) => {
      let changed = false;
      qsa("[data-marquee-item]", el).forEach((item) => {
        const until = Number(item.dataset.showUntil || 0);
        const flashOnly = item.hasAttribute("data-flash-only");
        const want = item.dataset.flashOffer || "any";
        let show = true;
        if (until && t >= until) show = false;
        if (flashOnly && (!st.active || (want !== "any" && want !== st.offer))) show = false;
        if (item.hasAttribute("data-hide-on-pack") && st.active && st.offer === "pack") show = false;
        if (item.hidden === show) { item.hidden = !show; changed = true; }
      });
      if (changed) measure();
    };
    registerCleanup(el, flash.onTick(applyVisibility));
    applyVisibility(flash.state(), flash.now());
    measure();
    // pause on touch (hover pause is CSS); resume when the finger lifts
    const pause = () => { track.style.animationPlayState = "paused"; };
    const resume = () => { track.style.animationPlayState = ""; };
    el.addEventListener("pointerdown", pause, { passive: true });
    ["pointerup", "pointercancel", "pointerleave"].forEach((ev) => el.addEventListener(ev, resume, { passive: true }));
    const onResize = debounce(measure, 150);
    window.addEventListener("resize", onResize);
    registerCleanup(el, () => window.removeEventListener("resize", onResize));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  /* ---------- snap slider (gallery, mini reviews, review cards) ------------ */
  function initSlider(root) {
    const track = qs("[data-slider-track]", root);
    if (!track) return null;
    const slides = () => qsa("[data-slider-slide]", track);
    const dots = qsa("[data-slider-dot]", root);
    const prev = qs("[data-slider-prev]", root);
    const next = qs("[data-slider-next]", root);
    let index = 0;

    const count = () => slides().length;
    const current = () => {
      const w = track.clientWidth || 1;
      const first = slides()[0];
      const step = first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) : w;
      return Math.round(track.scrollLeft / (step || w));
    };
    const paint = () => {
      dots.forEach((d, i) => {
        if (i === index) d.setAttribute("aria-current", "true"); else d.removeAttribute("aria-current");
      });
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= count() - 1;
      slides().forEach((s, i) => s.classList.toggle("is-active", i === index));
    };
    const goTo = (i, smooth = true) => {
      const n = count();
      if (!n) return;
      index = Math.max(0, Math.min(n - 1, i));
      const target = slides()[index];
      if (!target) return;
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: smooth && !REDUCED.matches ? "smooth" : "auto" });
      paint();
    };
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => { const c = current(); if (c !== index) { index = c; paint(); } });
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    registerCleanup(root, () => track.removeEventListener("scroll", onScroll));
    dots.forEach((d, i) => d.addEventListener("click", () => goTo(i)));
    if (prev) prev.addEventListener("click", () => goTo(index - 1));
    if (next) next.addEventListener("click", () => goTo(index + 1));
    track.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); goTo(index + 1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); goTo(index - 1); }
    });
    paint();
    const api = {
      goTo,
      goToMedia(id) {
        const i = slides().findIndex((s) => String(s.dataset.mediaId) === String(id));
        if (i >= 0) goTo(i);
      },
      get index() { return index; },
    };
    root.ovxSkiSlider = api;
    return api;
  }

  /* ---------- 6.4 bundle engine + cart ------------------------------------- */
  function initMain(root) {
    const cfgNode = qs("[data-ovx-ski-config]", root);
    if (!cfgNode) return;
    let cfg;
    try { cfg = JSON.parse(cfgNode.textContent || "{}"); } catch (e) { return; }
    const variants = cfg.variants || [];
    const byId = new Map(variants.map((v) => [String(v.id), v]));
    const tiers = cfg.tiers || [];
    const strings = cfg.strings || {};
    const money = (c) => formatMoney(c, cfg.money_format);

    const gallery = qs("[data-ovx-ski-gallery]", root)?.ovxSkiSlider || null;
    const tierRadios = qsa("input[data-tier-key]", root);
    const tierEls = new Map(qsa("[data-tier-card]", root).map((el) => [el.dataset.tierCard, el]));
    const addons = qs("[data-ovx-ski-addons]", root);
    const errorBox = qs("[data-ovx-ski-error]", root);
    const buyButtons = qsa("[data-ovx-ski-atc]", root);
    const sticky = qs("[data-ovx-ski-sticky]", root);

    const tierCfg = (key) => tiers.find((t) => t.key === key);
    const selectedKey = () => tierRadios.find((r) => r.checked)?.dataset.tierKey || tiers.find((t) => t.preselected)?.key || tiers[0]?.key;
    // A tier's gift row is live always (Friends Pack) or only on free-gift flash days (2-mask tier).
    const giftLive = (t) => !!(t && t.gift && t.gift.available && (!t.gift.flash_only || ns.flash.gift));

    // ----- tier math: fixed totals per currency from the shop metafield (same table as the discount function)
    function tierTotals(key) {
      const t = tierCfg(key);
      if (!t) return null;
      const flashOn = ns.flash.pack && t.flash_total > 0; // flash pack prices only on Friends Pack days
      const total = flashOn ? t.flash_total : t.total;
      const strike = t.strike;
      const save = strike > 0 && total < strike ? Math.round(100 * (1 - total / strike)) : 0;
      return { total, strike, save, each: Math.round(total / t.qty) };
    }
    function paintTier(key) {
      const el = tierEls.get(key);
      const t = tierCfg(key);
      if (!el || !t) return;
      const r = tierTotals(key);
      const totalEl = qs("[data-tier-total]", el), strikeEl = qs("[data-tier-strike]", el), lineEl = qs("[data-tier-line]", el), saveEl = qs("[data-tier-save]", el);
      if (totalEl) totalEl.textContent = money(r.total);
      if (strikeEl) { strikeEl.textContent = money(r.strike); strikeEl.hidden = r.strike <= r.total; }
      if (saveEl) { saveEl.textContent = (strings.save || "Save {{ percent }}%").replace("{{ percent }}", r.save); saveEl.hidden = r.save <= 0; }
      if (lineEl && t.qty > 1) lineEl.textContent = (strings.per_mask || "{{ count }} masks, {{ each }} each").replace("{{ count }}", t.qty).replace("{{ each }}", money(r.each));
    }
    function paintBadge() {
      const badge = qs("[data-ovx-ski-save-up-to]", root);
      if (!badge) return;
      const max = Math.max(0, ...tiers.map((t) => tierTotals(t.key).save));
      badge.textContent = (strings.save_up_to || "Save up to {{ percent }}%").replace("{{ percent }}", max);
      badge.hidden = max <= 0;
    }
    function currentTotalText(key) {
      const el = tierEls.get(key);
      return qs("[data-tier-total]", el)?.textContent?.trim() || "";
    }

    // ----- selection
    function selectTier(key, { announce = true } = {}) {
      tierEls.forEach((el, k) => el.classList.toggle("ovx-ski-tier--selected", k === key));
      const el = tierEls.get(key);
      if (addons && el) {
        const slot = qs("[data-tier-addons-slot]", el);
        if (slot && addons.parentElement !== slot) slot.appendChild(addons);
      }
      updateGiftRows();
      paintSticky();
      if (announce && window.plausible) {
        try { window.plausible("Bundle Selected", { props: { tier: key } }); } catch (e) { /* noop */ }
      }
    }
    function updateGiftRows() {
      const key = selectedKey();
      const t = tierCfg(key);
      const live = giftLive(t);
      // gift bars (flash-only ones toggle live with the window)
      tierEls.forEach((el, k) => {
        const bar = qs("[data-tier-gift]", el);
        if (bar) bar.hidden = !giftLive(tierCfg(k));
      });
      // hide the add-on row of the product already included as the gift
      qsa("[data-addon-row]", root).forEach((row) => {
        const handle = row.dataset.productHandle;
        const isGift = live && t.gift && t.gift.product_handle === handle;
        row.hidden = isGift;
        if (isGift) { const inp = qs("input", row); if (inp) { inp.checked = false; inp.setAttribute("aria-checked", "false"); } }
      });
      if (typeof syncUpsell === "function") syncUpsell();
    }
    // Friends Pack upsell: only while the PAID Mystery Gift toggle is on in the
    // 2-mask tier, because that is when the 3-mask price is barely more (or in a
    // flash sale, less) than what the cart already costs.
    function syncUpsell() {
      qsa("[data-ovx-ski-upsell]", root).forEach((up) => {
        const tierEl = up.closest("[data-tier-key-el]") || up.closest(".ovx-ski-tier");
        const row = tierEl && qs(`[data-addon-row][data-product-handle="${up.dataset.giftHandle}"]`, tierEl);
        const inp = row && qs("input", row);
        const on = !!(inp && inp.checked && !row.hidden && tierEl.classList.contains("ovx-ski-tier--selected"));
        up.hidden = !on;
      });
    }
    qsa("[data-ovx-ski-upsell]", root).forEach((up) => {
      up.addEventListener("click", () => {
        const r = qsa("input[data-tier-key]", root).find((x) => Number(x.value) === 3) ||
                  qsa("input[data-tier-key]", root)[2];
        if (r) { r.checked = true; r.dispatchEvent(new Event("change", { bubbles: true })); }
      });
    });
    qsa("[data-addon-row] input", root).forEach((i) => i.addEventListener("change", syncUpsell));
    tierRadios.forEach((r) => r.addEventListener("change", syncUpsell));
    registerCleanup(root, flash.onChange(() => syncUpsell()));
    setTimeout(syncUpsell, 0);

    tierRadios.forEach((r) => r.addEventListener("change", () => r.checked && selectTier(r.dataset.tierKey)));
    qsa("input[role='switch']", root).forEach((sw) => {
      sw.setAttribute("aria-checked", sw.checked ? "true" : "false");
      sw.addEventListener("change", () => sw.setAttribute("aria-checked", sw.checked ? "true" : "false"));
    });
    registerCleanup(root, flash.onChange(() => { updateGiftRows(); tiers.forEach((t) => paintTier(t.key)); paintBadge(); paintSticky(); }));

    // ----- style selects
    qsa("select[data-style-select]", root).forEach((sel) => {
      sel.addEventListener("change", () => {
        const v = byId.get(sel.value);
        const thumb = qs(`[data-style-thumb][data-tier="${sel.dataset.tier}"][data-slot="${sel.dataset.slot}"]`, root);
        if (thumb && v) {
          const img = qs("img", thumb);
          if (img && v.image) { img.src = v.image; img.srcset = ""; img.alt = v.title; }
        }
        const pdLabel = sel.closest(".ovx-ski-pd, .ovx-ski-styles__row")?.querySelector("[data-pd-label]");
        if (pdLabel && v) pdLabel.textContent = v.title;
        if (cfg.sync_gallery && gallery && v && v.media_id) gallery.goToMedia(v.media_id);
        paintTier(sel.dataset.tier);
        paintSticky();
      });
    });

    // ----- sticky
    function paintSticky() {
      if (!sticky) return;
      const key = selectedKey();
      const t = tierCfg(key);
      const label = qs("[data-sticky-label]", sticky);
      if (label && t) label.textContent = `${t.label} · ${currentTotalText(key)}`;
    }
    if (sticky) {
      const mainBtn = qs("[data-ovx-ski-buy]", root);
      const footer = qs(".ox-footer, footer");
      if (mainBtn) {
        // Geometry is read on scroll (rAF-throttled) rather than via IntersectionObserver:
        // a fast jump past the button never produces an "intersecting" frame, so IO would
        // never fire and the bar would stay hidden.
        let raf = 0;
        const update = () => {
          raf = 0;
          const b = mainBtn.getBoundingClientRect();
          const btnOut = b.bottom < 0;
          const footerIn = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
          sticky.classList.toggle("ovx-ski-sticky--visible", btnOut && !footerIn && !ns.cartBusy);
        };
        const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll, { passive: true });
        registerCleanup(root, () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); });
        update();
      }
    }

    // ----- add to cart
    function buildItems() {
      const key = selectedKey();
      const t = tierCfg(key);
      const items = [];
      const map = new Map();
      const rows = qsa(`select[data-style-select][data-tier="${key}"]`, root);
      if (rows.length) {
        rows.forEach((s) => { const id = Number(s.value); map.set(id, (map.get(id) || 0) + 1); });
      } else if (variants[0]) {
        map.set(Number(variants[0].id), t ? t.qty : 1);
      }
      map.forEach((q, id) => items.push({ id, quantity: q, properties: { _ovx_bundle: key } }));
      qsa("[data-addon-row]", root).forEach((row) => {
        const inp = qs("input", row);
        if (inp && inp.checked && !row.hidden && Number(inp.dataset.variantId)) items.push({ id: Number(inp.dataset.variantId), quantity: 1 });
      });
      if (giftLive(t)) {
        const gp = { _ovx_gift: key };
        items.push({ id: Number(t.gift.variant_id), quantity: 1, properties: gp });
      }
      return items;
    }
    async function addToCart() {
      if (ns.cartBusy) return;
      const items = buildItems();
      if (!items.length) return;
      ns.cartBusy = true;
      if (errorBox) errorBox.textContent = "";
      buyButtons.forEach((b) => { b.setAttribute("aria-busy", "true"); b.disabled = true; });
      try {
        const res = await fetch(cfg.routes.cart_add_url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/javascript", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items }),
        });
        let data = null;
        try { data = await res.json(); } catch (e) { /* noop */ }
        if (!res.ok) {
          const msg = (data && (data.description || data.message)) || strings.error || "Couldn't add to cart. Please try again.";
          throw new Error(msg);
        }
        const mode = cfg.after_add || "drawer";
        if (mode === "checkout") { window.location.href = `${cfg.routes.root || "/"}checkout`; return; }
        if (mode === "cart") { window.location.href = cfg.routes.cart_url || "/cart"; return; }
        if (window.orevyxCart) {
          window.orevyxCart.open();
          await window.orevyxCart.refresh();
        } else {
          window.location.href = cfg.routes.cart_url || "/cart";
        }
      } catch (err) {
        if (errorBox) errorBox.textContent = err && err.message ? err.message : (strings.error || "Couldn't add to cart. Please try again.");
      } finally {
        ns.cartBusy = false;
        buyButtons.forEach((b) => { b.removeAttribute("aria-busy"); b.disabled = !!cfg.sold_out; });
      }
    }
    buyButtons.forEach((b) => b.addEventListener("click", (e) => { e.preventDefault(); addToCart(); }));
    // Belt and braces: the product form only hosts payment terms - never submit it.
    const form = qs("form[data-ovx-ski-form]", root);
    if (form) form.addEventListener("submit", (e) => { e.preventDefault(); e.stopPropagation(); addToCart(); });

    // ----- initial paint (server already rendered the default state; repaint covers a day flip after caching)
    tiers.forEach((t) => paintTier(t.key));
    paintBadge();
    selectTier(selectedKey(), { announce: false });
    ns.addToCart = addToCart;
  }

  /* ---------- cart nudge (drawer): add the missing face + gift in one tap ----- */
  let nudgeBound = false;
  function bindNudge() {
    if (nudgeBound) return;
    nudgeBound = true;
    document.addEventListener("click", async (e) => {
      const btn = e.target.closest && e.target.closest("[data-ovx-ski-nudge]");
      if (!btn) return;
      e.preventDefault();
      let items = [];
      try { items = JSON.parse(btn.dataset.items || "[]"); } catch (err) { items = []; }
      if (!items.length || btn.getAttribute("aria-busy") === "true") return;
      btn.setAttribute("aria-busy", "true");
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        const res = await fetch(`${root}cart/add`, { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/javascript", "X-Requested-With": "XMLHttpRequest" }, body: JSON.stringify({ items }) });
        if (!res.ok) throw new Error("add failed");
        if (window.orevyxCart) await window.orevyxCart.refresh();
      } catch (err) {
        btn.removeAttribute("aria-busy");
      }
    });
    // keep the nudge amount in step with a live flash flip (the flash price only exists on
    // Friends Pack days); when the day's offer changes, re-fetch the drawer so the gift meter,
    // its target and the button match what the checkout will do from now on.
    let lastOffer = null;
    flash.onChange((st) => {
      qsa("[data-nudge-text]").forEach((t) => { t.textContent = st.pack ? t.dataset.textFlash : t.dataset.textNormal; });
      if (lastOffer !== null && lastOffer !== st.offer && window.orevyxCart && window.orevyxCart.refresh) {
        try { window.orevyxCart.refresh(); } catch (e) { /* noop */ }
      }
      lastOffer = st.offer;
    });
  }

  /* ---------- "Arrives" line: delivery window computed in the visitor's browser ---------- */
  // Dates on which nothing ships or moves, on top of Saturdays and Sundays. ONE list, edit here.
  // Given by the owner on 2026-09-25 (US holidays as observed by the carriers). Format YYYY-MM-DD.
  const NO_SHIP_DATES = [
    "2026-10-12",
    "2026-11-11",
    "2026-11-26",
    "2026-12-25",
    "2026-12-28",
    "2027-01-01",
    "2027-01-18",
    "2027-02-15",
  ];
  const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const isoLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  // en-US short month + day, "Oct 5" / "Nov 4", whatever the visitor's locale.
  const fmtMonthDay = (d) => {
    try { return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(d); }
    catch (e) { return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`; }
  };
  // The n-th business day AFTER `from` (local calendar): day 1 is the first weekday after `from`
  // that is not in NO_SHIP_DATES. 2026-09-25 + 6 = Oct 5, + 10 = Oct 9; 2026-10-02 + 6 = Oct 13 (Oct 12 skipped), + 10 = Oct 19.
  const addBusinessDays = (from, n) => {
    const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    let count = 0, guard = 0;
    while (count < n && guard++ < 400) {
      d.setDate(d.getDate() + 1);
      const dow = d.getDay();
      if (dow === 0 || dow === 6) continue;
      if (NO_SHIP_DATES.indexOf(isoLocal(d)) !== -1) continue;
      count++;
    }
    return d;
  };
  // { from: "Oct 5", to: "Oct 9" } for an order placed on `date` (a Date, or "YYYY-MM-DD"; default today).
  function arrivesRange(minDays, maxDays, date) {
    let base;
    if (typeof date === "string") { const [y, m, dd] = date.split("-").map(Number); base = new Date(y, m - 1, dd); }
    else base = date instanceof Date ? date : new Date();
    const lo = Math.max(1, Math.round(Number(minDays) || 0));
    const hi = Math.max(lo, Math.round(Number(maxDays) || 0));
    const a = addBusinessDays(base, lo), b = addBusinessDays(base, hi);
    return { from: fmtMonthDay(a), to: fmtMonthDay(b), fromISO: isoLocal(a), toISO: isoLocal(b) };
  }
  ns.arrivesRange = arrivesRange;
  function initArrives(el) {
    const text = qs("[data-arrives-text]", el);
    if (!text) return;
    const min = Number(el.dataset.minDays) || 6;
    const max = Number(el.dataset.maxDays) || min;
    const tpl = el.dataset.template || "Arrives [from] - [to]";
    const tplSingle = el.dataset.templateSingle || "Arrives [date]";
    let paintedFor = "";
    const paint = () => {
      const today = isoLocal(new Date());
      if (today === paintedFor) return;
      try {
        const r = arrivesRange(min, max);
        text.textContent = r.fromISO === r.toISO ? tplSingle.replace("[date]", r.from) : tpl.replace("[from]", r.from).replace("[to]", r.to);
        el.setAttribute("data-arrives-ready", "1");
        paintedFor = today;
      } catch (e) { /* the server-rendered "Arrives in 6-10 business days" stays */ }
    };
    paint();
    // a tab left open past midnight repaints for the new day (cheap: one string compare a minute)
    const timer = setInterval(paint, 60000);
    registerCleanup(el, () => clearInterval(timer));
  }

  /* ---------- videos: ambient autoplay in view, lightbox with sound ------------ */
  function initVideos(root) {
    const cards = qsa("[data-video-card]", root);
    if (!cards.length) return;
    const activate = (video) => {
      if (!video || video.dataset.activated) return;
      video.dataset.activated = "1";
      qsa("source", video).forEach((s) => { if (s.dataset.src) { s.src = s.dataset.src; s.removeAttribute("data-src"); } });
      video.load();
    };
    const play = (v) => { if (REDUCED.matches || !v) return; const p = v.play(); if (p && p.catch) p.catch(() => {}); };
    if ("IntersectionObserver" in window) {
      const near = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) activate(qs("[data-video]", en.target)); }), { rootMargin: "200px 0px" });
      const inView = new IntersectionObserver((entries) => entries.forEach((en) => { const v = qs("[data-video]", en.target); if (!v) return; if (en.isIntersecting && en.intersectionRatio >= 0.5) { activate(v); play(v); } else v.pause(); }), { threshold: [0, 0.5, 1] });
      cards.forEach((c) => { near.observe(c); inView.observe(c); });
      registerCleanup(root, () => { near.disconnect(); inView.disconnect(); });
    } else {
      cards.forEach((c) => { const v = qs("[data-video]", c); activate(v); play(v); });
    }
    const box = qs("[data-video-lightbox]", root), big = qs("[data-video-big]", root);
    let opener = null;
    const close = () => { if (!box || box.hidden) return; big.pause(); qsa("source", big).forEach((s) => s.remove()); big.removeAttribute("src"); big.load(); box.hidden = true; document.documentElement.style.overflow = ""; if (opener) opener.focus({ preventScroll: true }); };
    cards.forEach((c) => qs("[data-video-open]", c)?.addEventListener("click", (e) => {
      if (!box || !big) return;
      const v = qs("[data-video]", c); activate(v);
      opener = e.currentTarget;
      qsa("source", big).forEach((s) => s.remove());
      qsa("source", v).forEach((s) => { const n = document.createElement("source"); n.src = s.src || s.dataset.src; n.type = s.type; big.appendChild(n); });
      big.poster = v.poster; big.muted = false; big.load();
      box.hidden = false; document.documentElement.style.overflow = "hidden";
      const p = big.play(); if (p && p.catch) p.catch(() => {});
      qs("[data-video-close]", box)?.focus({ preventScroll: true });
    }));
    qsa("[data-video-close]", root).forEach((b) => b.addEventListener("click", close));
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    registerCleanup(root, () => { document.removeEventListener("keydown", onKey); close(); });
  }

  /* ---------- press feedback ------------------------------------------------ */
  let pressBound = false;
  function bindPress() {
    if (pressBound) return;
    pressBound = true;
    const clear = () => qsa(".ovx-ski-press.is-pressed").forEach((el) => el.classList.remove("is-pressed"));
    document.addEventListener("pointerdown", (e) => {
      const el = e.target.closest && e.target.closest(".ovx-ski-press");
      if (el && !el.disabled) el.classList.add("is-pressed");
    }, { passive: true });
    ["pointerup", "pointercancel", "dragend"].forEach((ev) => document.addEventListener(ev, clear, { passive: true }));
    document.addEventListener("pointerleave", clear, { passive: true });
    window.addEventListener("blur", clear);
  }

  /* ---------- init / lifecycle --------------------------------------------- */
  function initWithin(scope) {
    bindPress();
    flash.load();
    qsa("[data-ovx-ski-bar]", scope).forEach(initBar);
    qsa("[data-ovx-ski-marquee]", scope).forEach(initMarquee);
    qsa("[data-ovx-ski-slider]", scope).forEach(initSlider);
    // videos rail: hide the arrows and centre the row when nothing overflows
    qsa("[data-videos-rail]", scope).forEach((rail) => {
      const track = qs("[data-slider-track]", rail);
      if (!track) return;
      const sync = () => rail.classList.toggle("no-overflow", track.scrollWidth - track.clientWidth < 4);
      track.addEventListener("scroll", sync, { passive: true });
      window.addEventListener("resize", sync, { passive: true });
      setTimeout(sync, 0); sync();
    });
    qsa("[data-ovx-ski-main]", scope).forEach(initMain);
    qsa("[data-ovx-ski-arrives]", scope).forEach(initArrives);
    qsa("[data-ovx-ski-videos]", scope).forEach(initVideos);
    bindNudge();
    flash.start();
  }
  function boot() {
    initWithin(document);
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener("shopify:section:load", (e) => { runCleanups(e.target); initWithin(e.target); });
      document.addEventListener("shopify:section:unload", (e) => { runCleanups(e.target); qsa("[data-ovx-ski-bar],[data-ovx-ski-marquee],[data-ovx-ski-slider],[data-ovx-ski-main],[data-ovx-ski-arrives],[data-ovx-ski-videos]", e.target).forEach(runCleanups); });
      document.addEventListener("shopify:block:select", (e) => {
        const slider = e.target.closest("[data-ovx-ski-slider]");
        const slide = e.target.closest("[data-slider-slide]");
        if (slider && slide && slider.ovxSkiSlider) slider.ovxSkiSlider.goTo(qsa("[data-slider-slide]", slider).indexOf(slide));
      });
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot); else boot();
})();
